import { IsomorphicLink } from '@flipster/react/design-utils/isomorphic-link';
import { css } from '@flipster/styled-system/css';

const TermsAndConditions = () => {
  return (
    <div
      className={css({
        textStyle: 'Text/M/Regular',
        color: 'content.default.level-2',
        '& strong, h4': {
          textStyle: 'Text/M/Strong',
        },
        '& h4, p, ul': {
          my: 16,
        },
        '& li': {
          listStyle: 'disc',
          ml: 24,
        },
      })}
    >
      <p>
        These Premium Earn Terms (<strong>“PE Terms”</strong>) shall apply to participants of Premium Earn. Your
        participation in Premium Earn shall constitute your agreement to the PE Terms.
      </p>
      <p>
        All capitalised terms and references used in these PE Terms that are defined and construed in the Flipster Terms
        of Use (the <strong>“Terms of Use”</strong>) but are not defined or construed in these PE Terms shall have the
        same meaning and construction in these PE Terms as in the Terms of Use. For the avoidance of doubt, where a term
        is defined both in the Terms of Use and in these PE Terms, the definition provided in these PE Terms shall (for
        the purposes of Premium Earn only) apply.
      </p>
      <p>
        The PE Terms shall be supplemental to and are to be read together with the Trading Platform’s Terms of Use and
        Derivatives Trading Rules (where relevant). In the event of any conflict or inconsistency between the terms
        herein, and the Terms of Use or the Derivatives Trading Rules as the case may be, the terms herein shall prevail
        with respect to the products contemplated hereunder unless expressly stated otherwise.
      </p>
      <p>
        Unless otherwise expressly agreed by us in writing, this version of these PE Terms shall replace and supersede
        all previous versions of these PE Terms that may have been accepted by you or otherwise made available on the
        Trading Platform.
      </p>
      <p>
        These PE Terms are binding between you and Flipster Corp (<strong>“we”</strong>, <strong>“us” </strong>
        or <strong>“our”</strong>), a company incorporated under the laws of the Republic of Panama.
      </p>
      <p>Premium Earn is not available to users from Prohibited Jurisdictions.</p>
      <h4>1. Definitions and Interpretation</h4>
      <ul>
        <p>1.1. The following definitions are applicable to these Premium Earn:</p>
        <li>
          <strong>“Applicable Law”</strong> means any law, statute, bye-law, regulation, order, regulatory policy,
          guidance or industry code or practice, rule of court or directives or requirements of any governmental or
          regulatory authority, or any delegated or subordinated legislation or notice, each as amended and promulgated
          from time to time, that may be directly or indirectly related to or implicated by the relevant activities or
          transactions carried out or otherwise arising under Premium Earn.
        </li>
        <li>
          <strong>"Derivatives Trading Rules”</strong> shall have the same meaning as set out in the Trading Platfom’s
          <IsomorphicLink
            href='https://flipster.io/en/policies/derivatives'
            external
            className={css({
              textStyle: 'Text/M/Regular',
              color: 'content.default.level-2',
              textDecoration: 'underline',
              px: 6,
            })}
          >
            Derivatives Trading Terms;
          </IsomorphicLink>
        </li>
        <li>
          <strong>“Lockup Period”</strong> shall have the meaning as set out in Clause 3.3.
        </li>
        <li>
          <strong>"Loss"</strong> means any and all losses, damages, costs, charges and/or expenses, including, but not
          limited to, legal fees on a full indemnity basis, cost of funding, losses or costs incurred as a result of the
          terminating, liquidating or re-establishing of any hedge or related trading position, loss of profit, loss of
          revenue, loss of opportunity, consequential, unforeseeable, special or indirect damages or expenses.
        </li>
      </ul>
      <p>
        1.2. The headings of each provision in these PE Terms are inserted for convenience only and shall not affect the
        construction of these PE Terms. Expressions in the singular form shall include the plural and vice versa, and
        all references to the masculine genders shall include the female and neuter genders and vice versa.
      </p>
      <p>
        1.3. The words “include” or “including” shall be deemed to be followed by “without limitation” or “but not
        limited to” whether or not they are followed by such phrases or words of like import, and “otherwise” shall not
        be construed as limited by words with which it is associated.
      </p>
      <h4>2. General</h4>
      <p>
        2.1. By accessing your Account and using or otherwise adding USDT to Premium Earn, you agree to be bound by (i)
        these PE Terms; (ii) the Terms of Use; and (iii) the Derivatives Trading Rules (where relevant); and (iv) any
        other rules that apply in relation to Premium Earn provided by us from time to time, which shall include,
        without limitation, any applicable Guides (as defined in the Terms of Use), terms of service, and/or protocols
        that we determine at our discretion to form part of these PE Terms in our sole and absolute discretion (as
        available here or on such other webpage as notified by us from time to time) (collectively, the{' '}
        <strong>“PE Rules”</strong>).
      </p>
      <p>2.2. We may, from time to time, amend the PE Terms or the PE Rules in our sole discretion.</p>
      <p>
        2.3. Nothing in these PE Terms or on our Trading Platform or otherwise obliges us to provide Premium Earn and
        its related information, rewards, features, or functions to you and we may at any time change, suspend,
        terminate, restrict or discontinue provision of Premium Earn, or any information, rewards, features or functions
        available through the Trading Platform either temporarily or on a permanent basis, in each case, at any time in
        our sole and absolute discretion, without providing any reason or any Notice to you thereof.
      </p>
      <p>
        2.4. Nothing contained on the Trading Platform is to be construed as an offer, an invitation to offer or a
        recommendation to enter into any transaction (including to participate in Premium Earn in any way) which can be
        accepted by you to create contractual obligations without further action by us. You acknowledge that no advice
        is provided by us to you with respect to your use of the Trading Platform (including, without limitation, advice
        relating to the execution of any transaction, or any legal, tax or accounting advice regarding the suitability
        or profitability of any security, investment or transaction). You also acknowledge that the Trading Platform
        provides you with information which is general in nature, and that such information shall not be construed as
        financial advice from us to you, or as an offer, solicitation or recommendation of securities or other financial
        products from us to you. Such information is not personalized or in any way tailored to reflect your particular
        investment objectives, financial situation or needs. You acknowledge that any transaction(s), features(s),
        product(s), service(s) or function(s) available on the Trading Platform may not be appropriate for all investors
        and that you should take steps to ensure that, before entering into any transaction, you fully understand the
        transaction and have made an independent assessment (and/or have sought third party advice from your own
        advisors) of the appropriateness of the transaction(s) in light of your own objectives and circumstances
        (including the possible risks and benefits of entering into such Transaction). If you decide to enter into any
        transaction, you shall do so in reliance on your own judgement.
      </p>
      <p>
        2.5. You acknowledge that there are legal requirements in various countries which may restrict the products and
        services that we can lawfully provide to you. Accordingly, some products, services, features, and functionality
        within the Trading Platform, including in relation to Premium Earn, may not be available or may be restricted in
        certain jurisdictions or regions or to certain users. You shall be responsible for understanding and observing
        any restrictions and/or requirements imposed with respect to the access to and use of Premium Earn in each
        jurisdiction from which the Trading Platform is accessed by you or on your behalf. We reserve the right to
        change, modify or impose additional restrictions with respect to the access to and use of the Trading Platform
        and Premium Earn from time to time in our sole discretion without Notice.
      </p>
      <h4>3. Premium Earn participation</h4>
      <p>3.1. You may participate in Premium Earn through our Trading Platform by adding USDT to Premium Earn.</p>
      <p>
        3.2. Upon adding or allocating USDT to Premium Earn, your USDT in Premium Earn{' '}
        <strong>
          can only be held in your own Account on the Platform Wallet and cannot be withdrawn or transferred.
        </strong>{' '}
        However, you will still be able to use our USDT in Premium Earn as margin for trading of Derivatives on our
        Trading Platform. You will also be able to earn rewards on your USDT in Premium Earn.
      </p>
      <p>
        3.3. When you want to remove or reduce your allocation of USDT from Premium Earn, you can elect to convert or
        withdraw your USDT from Premium Earn through the Trading Platform. Upon such action initiated by you through the
        Trading Platform, a 24-hour lock-up period (<strong>“Lockup Period”</strong>) will commence. At the end of the
        Lockup Period, USDT allocated to Premium Earn will automatically be released from the Premium Earn program and
        reflected in your Platform Wallet as USDT.
      </p>
      <p>
        3.4. We have the right to implement any limits or restrictions on the Premium Earn program, including any
        minimum or maximum amounts that users can allocate to Premium Earn (whether to specific users or in aggregate to
        all users), or any other transaction limits from time-to-time in our sole discretion. We may implement such
        limits or restrictions directly through the Trading Platform, without prior notice to you.
      </p>
      <p>
        3.5. We reserve the right temporarily or permanently cease the purchase or offering of Premium Earn whether to
        you or to all users or any group of users, as we determine in our sole and absolute discretion.
      </p>
      <h4>4. Rewards and Trading</h4>
      <p>
        4.1. Unless otherwise notified to you, and subject always to these Premium Earn Terms, the USDT which you hold
        in Premium Earn is eligible to earn rewards at the rates and through the calculation and settlement methods as
        announced through our Trading Platform from time to time.
      </p>
      <p>
        4.2. All rewards for Premium Earn are paid out from our marketing budget and are at our sole and absolute
        discretion. While we will endeavor to give you prior notice regarding any changes in rewards for the Premium
        Earn program, we reserve the right to vary or remove rewards at any time, with or without prior notice to you.
      </p>
      <p>
        4.3. Your USDT in Premium Earn may be used as margin for trading of certain Derivatives trading pairs on the
        Trading Platform, as made available on the Trading Platform from time to time at our sole discretion. Such
        trading shall be subject to the Derivatives Trading Rules.
      </p>
      <p>4.4. We may from time to time at our sole discretion and without prior notice to you:</p>
      <p>
        (i) Suspend or cancel any functions, features, or uses of USDT held in Premium Earn, including but not limited
        to the use of USDT held in Premium Earn for Derivatives Trading in accordance with this Section; or
      </p>
      <p>(ii) provide any additional uses for USDT held in Premium Earn through our Trading Platform.</p>
      <h4>5. Premium Earn Redemption</h4>
      <p>
        5.1. As set out in Clause 3.3 above, when you want to remove or reduce your allocation of USDT from Premium
        Earn, you can elect to convert or withdraw your USDT from Premium Earn through the Trading Platform. Upon such
        action initiated by you through the Trading Platform, a 24-hour lock-up period (<strong>“Lockup Period”</strong>
        ) will commence. At the end of the Lockup Period, USDT allocated to Premium Earn will automatically be released
        from the Premium Earn program and reflected in your Platform Wallet as USDT.
      </p>
      <p>
        5.2. Notwithstanding Clause 5.1, we reserve the right to temporarily or permanent suspend, or fully or partially
        restrict redemptions from Premium Earn in the following situations:
      </p>
      <p>
        (i) where we consider that (or have reason to suspect that) you have violated these Premium Earn, our Terms of
        Use, or Derivatives Trading Rules, or any other terms, rules, or guidelines of our Trading Platform;
      </p>
      <p>
        (ii) where we consider that (or have reason to suspect that) there are any issues, violations, or suspicious
        activities (including but not limited to in relation to money laundering, terrorist financing, fraud, sanctions,
        or any other criminal behaviour) relating to your Account including but not limited to in relation to customer
        due diligence, your account usage, or your transactions;
      </p>
      <p>
        (iii) where we are required to or where we deem it necessary do so to facilitate compliance with any Applicable
        Laws, or any request or order from a court or from any regulatory, governmental, tax, or law enforcement
        authority;
      </p>
      <p>
        (iv) where we are required to or where we deem it necessary to do so to protect the security, integrity, or
        reputation of our Trading Platform, us, or any of our affiliates; or
      </p>
      <p>(v) in case of any force majeure event.</p>
      <p>
        5.3. You hereby specifically undertake that you fully understand and agree with the redemption terms for Premium
        Earn set out herein, and that we shall not be liable for any claims, losses, or damages arising directly or
        indirectly from any delay or failure in processing redemption requests in accordance with these PE Terms.
      </p>
      <h4>6. Representations, Warranties, Consents, and Undertakings</h4>
      <p>6.1. You represent, warrant, agree and/or undertake (as applicable) that:</p>
      <p>
        (i) you have full capacity and authority, and (where relevant) you have obtained (and will maintain) and are in
        compliance with all necessary authorisations, consents, licences or approvals (whether under Applicable Law or
        otherwise) required to: (a) accept and agree to these PE Terms; (b) participate in, perform transactions, and
        utilize the Premium Earn program;
      </p>
      <p>
        (ii) you have made your own independent decision to: (a) accept these PE Terms; (b) open and maintain an
        Account; (c) participate in, perform transactions, and utilize the Premium Earn program; and (d) determine that
        the participation in Premium Earn is suitable or appropriate for you, based on your own judgment and/or advice
        from your advisors;
      </p>
      <p>
        (iii) you are not relying on any communication (whether written or oral) from us: (a) as investment advice; (b)
        as a recommendation to participate in Premium Earn or to enter into any transaction; or (c) as an assurance or
        guarantee of any expected results of participating in Premium Earn or any related transaction;
      </p>
      <p>
        <strong>
          (iv) you have fully read and understood these PE Terms and accept these Terms, including its conditions and
          limitations for each service, feature, or reward available to you in connection with Premium Earn;
        </strong>
      </p>
      <p>
        (v) you do not reside in any of the Prohibited Jurisdictions as set out in our Terms of Use, or in any other
        jurisdictions in which we have restricted the offering of Premium Earn and its related features;
      </p>
      <p>
        (vi) you are the ultimate and effective legal and beneficial owner of any digital assets you have allocated or
        added to Premium Earn, and that you will not act and are not acting as nominee, agent, or trustee for any other
        person;
      </p>
      <p>
        (vii) you are not aware of any charge, mortgage, lien, pledge, encumbrance or any other security interest over
        any part of the digital assets allocated or added by you to Premium Earn;
      </p>
      <p>
        (viii) except with our express written consent, no person other than you has any interest in your Account or the
        USDT allocated to Premium Earn within your Account;
      </p>
      <p>
        (ix) except with our express written consent, no person has or will have any security or other encumbrance over
        your or the USDT allocated to Premium Earn within your Account;
      </p>
      <p>
        (x) any order placed or any other transactions or dealings with respect to Premium Earn under your Account is
        solely and exclusively based on your own judgement, independent appraisal and investigation into the risk
        associated with such actions, as well as your independent determination of your actions being specifically
        suitable to you based on your own assessment of your financial resources, financial objectives and ability and
        willingness to take relevant risks;
      </p>
      <p>
        (xi) we shall not be under any duty or obligation to inquire into the purpose or propriety of any order placed
        or any other transactions or dealings with respect to Premium Earn under your Account;
      </p>
      <p>
        (xii) you have complied with and shall comply with all Applicable Laws in all jurisdictions relevant to your
        Account and Premium Earn, and any transaction or other service or facility or feature provided or made available
        by us to you;
      </p>
      <p>
        (xiii) you shall not knowingly or recklessly permit the use of Premium Earn, or any of our services, products,
        or features in a manner which may, in our opinion, bring us into disrepute, impair the dignity or degrade our
        good name; and
      </p>
      <p>
        (xiv) you shall not otherwise act in a manner which is substantially detrimental to our interests or the
        interests of any of our other users.
      </p>
      <p>
        6.2. The above representations, warranties, agreement and undertakings shall be deemed repeated whenever you
        place any order, take any actions, or enter into any transactions in relation to Premium Earn, and each time you
        log in to the Trading Platform.
      </p>
      <h4>7. General Exclusions and our reserved rights</h4>
      <p>
        7.1. Without limiting any of our other rights and without prejudice to any other Clauses in these PE Terms, we
        shall not be liable or responsible to you, or be deemed to have defaulted under or breached these PE Terms, for
        any failure, delay, interruption, or unavailability in the provision of any of our features, functions, or
        services in relation to Premium Earn or the performance of these PE Terms, if and to the extent the foregoing is
        caused by or results from or is connected to acts beyond our reasonable control, including, without limitation:
        (a) acts of God; (b) flood, fire, earthquake, pandemic or explosion; (c) war, invasion, hostilities (whether war
        is declared or not), terrorist, hacking or cyber threats, attacks or acts, or other civil unrest; (d) any Fork
        or Vulnerability affecting the underlying software protocols governing the operation of USDT or to any digital
        currency available to you for trading on the Trading Platform from time to time; (e) any laws, statutes,
        ordinances, rules, regulations, judgments, injunctions, orders, and decrees; (f) action by any nation or
        government, any state or other political subdivision thereof, any entity exercising legislative, judicial or
        administrative functions of or pertaining to government, including, without limitation, any government
        authority, agency, department, board, commission or instrumentality, and any court, tribunal or arbitrator(s) of
        competent jurisdiction; or (g) other circumstances affecting the supply of our goods and services.
      </p>
      <p>
        7.2. Participating in Premium Earn and the use of any platform product, service, feature or function in relation
        to Premium Earn is not suitable for everyone. You should closely examine these terms, your investment
        objectives, financial resources and risk tolerance to determine whether participating in Premium Earn is
        appropriate for you. You hereby fully acknowledge the risks associated with participating in Premium Earn, which
        includes but is not limited to the risks set out in the “Risk Disclosures” Section below.
      </p>
      <p>
        7.3. Derivatives trading is not suitable for everyone. You should examine your investment objectives, financial
        resources and risk tolerance to determine whether borrowing against your assets held with us is appropriate for
        you. You acknowledge that Derivatives trading with respect to Digital Assets is a highly speculative activity
        involving highly leveraged and rapidly fluctuating markets. You further understand and accept the significant
        risks associated with the use of margin and leverage and have determined that the use of margin and/or leverage
        is consistent with your investment objectives. Despite such risks, you acknowledge, understand and agree that
        you are willing and able to assume the financial risks and other hazards of Derivatives trading with respect to
        Digital Assets and agree that you will in no manner hold us responsible for Losses incurred by you under or in
        connection with these PE Terms, the Derivatives Trading Rules, the Terms of Use or otherwise. You acknowledge
        that trading in Derivatives involves the potential for profit as well as the risk of losing substantially more
        than your initial capital. You do not own or have any rights to the underlying instruments, which include
        Digital Assets and any rewards or yields generated from such Digital Assets.
      </p>
      <p>
        7.4. In addition to and without prejudice to any other right or remedy which we may have under these PE Terms,
        the Derivatives Trading Rules, the Terms of Use, Applicable Laws or otherwise, we shall not be liable to you in
        any respect for any Loss suffered by you if we have acted in good faith. We shall only be liable to you if a
        court of competent jurisdiction has determined that we have been fraudulent or acted in willful default.
      </p>
      <p>
        7.5. Notwithstanding anything in these PE Terms, we shall not be liable to you for any indirect or consequential
        Loss, or for any loss of anticipated profits or any punitive damages.
      </p>
      <h4>8. Risk Disclosures (YOUR ATTENTION IS PARTICULARLY DRAWN TO THIS CLAUSE)</h4>
      <p>
        8.1. Participating in Premium Earn carries risks. Such risks include, without limitation, the risks described in
        this “Risk Disclosures” Section.
      </p>
      <p>
        8.2. By accepting the PE Terms and using any of its related services, features, or functions, you expressly
        acknowledge, accept, and assume all the risks related to its use. You should participate in Premium Earn if you
        fully understand and are willing to accept the risks. The risks set out below area not intended to be exhaustive
        and merely serves as a summary of some of the risks that you may face from participating in Premium Earn.
      </p>
      <p>
        8.3. <strong>Risks related to restrictions on USDT held in Premium Earn.</strong> As set out in the “Premium
        Earn Redemption” Section above, your USDT held in Premium Earn can only be held in your own Account on the
        Platform Wallet and cannot be withdrawn or transferred. Your USDT held in Premium Earn is also subject to a
        Lockup Period upon your initiation of a withdrawal or redemption of USDT from the Premium Earn program. The
        terms relating to restrictions, rights, rewards, and permitted uses of USDT held in Premium Earn are as set out
        in these PE Terms, which may at any time be varied in accordance with the “Variation to these PE Terms” section
        below.
      </p>
      <p>
        8.4. <strong>Risks related to using USDT as margin for Derivatives trading on the Trading Platform.</strong> You
        hereby represent and confirm that you have read and accepted all the risks as set out in our Derivatives Trading
        Terms, and you hereby expressly acknowledge and accept that such risks similarly apply when you use USDT held in
        Premium Earn as margin for trading Derivatives on the Trading Platform.
      </p>
      <p>
        8.5. <strong>Risks related to your Account and the Trading Platform.</strong> You hereby represent and confirm
        that you have read and accepted all the risks as set out in our Terms of Use, and you hereby expressly
        acknowledge and accept that such risks similarly apply (with the necessary modifications) where you participate
        in Premium Earn through our Trading Platform.
      </p>
      <p>
        8.6. <strong>Risks related to regulatory uncertainty.</strong> The regulatory environment surrounding digital
        assets and the offering of this Premium Earn program and is functions and features is subject to change, and any
        adverse regulatory actions or new laws may impact the availability, operations and terms of the Premium Earn
        program, including potential disruptions, delays, or restrictions to or on redemption of USDT from the Premium
        Earn program.
      </p>
      <h4>9. Variation to these PE Terms</h4>
      <p>
        Clause 38 of the Terms of Use relating to variation of terms shall apply (with any necessary modifications) to
        these PE Terms.
      </p>
      <h4>10. Applicable Laws</h4>
      <p>
        Your relationship with us, the operation of your Account and the Trading Platform, the provision of Premium
        Earn, and the implementation of any such related transactions shall be subject to Applicable Laws. We may take
        or refrain from taking any action whatsoever, and you shall comply with, and shall do all things required by us
        in order to procure or ensure compliance with all Applicable Laws. You agree that we shall not be liable to you
        as a result of any action taken to comply with any Applicable Law.
      </p>
      <h4>11. Disputes, Governing Law, and Jurisdiction</h4>
      <p>Clause 42 of the Terms of Use shall apply (with any necessary modifications) to these PE Terms. </p>
    </div>
  );
};

export default TermsAndConditions;
